import { Card, Divider, Form, message, Modal } from "antd";
import React, { useCallback, useContext, useEffect, useState } from "react";
import HSelect from "../../../components/custom/input/select/Select";
import Input from "../../../components/custom/input/input/Input";
import HButton from "../../../components/custom/input/button/Button";
import { useVoiceNumbers } from "../../hooks/useVoiceNumbers";
import { useVoiceIntegId } from "../../utils/VoiceUtils";
import { AppContext } from "../../../context/AppContext";
import { usePusher } from "../../../context/PusherContext";
import "../../assets/css/numberVoiceConfig.css";
import { TWILIO_CALLER_ID_HELP_URL } from "../../../constants/AppConstants";
import { showFooterContentForSelect } from "../../../pages/utils/CommonVessels";
import HNotification from "../../../components/custom/notification/HNotification";

const CallerIdSettings = (props) => {
  const {
    callerId,
    setCallerId,
    setOutgoingCallerIdSid,
    userSpecific = false,
    defaultValue = null,
  } = props;
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [isCallerIdPopupVisible, setIsCallerIdPopupVisible] = useState(false);
  const [form] = Form.useForm();
  const [appProperties] = useContext(AppContext);
  const [callerIdList, setCallerIdList] = useState([]);
  const { verifyCallerId, getAllCallerId } = useVoiceNumbers();
  const { connect, subscribe, unSubscribe } = usePusher();

  const initPusherCallForThisUser = useCallback(async () => {
    const connectionSuccess = await connect(false, true);
    console.log("debug-1 connectionSuccess", connectionSuccess);
  }, [connect]);

  useEffect(() => {
    if (appProperties?.currentUser?.osyncUserId) {
      initPusherCallForThisUser();
    }
  }, [appProperties?.currentUser?.osyncUserId, initPusherCallForThisUser]);

  const fetchCallerIds = useCallback(async () => {
    const response = await getAllCallerId();
    if (response && Array.isArray(response)) {
      setCallerIdList(response);
    }
  }, [getAllCallerId]);

  useEffect(() => {
    if (callerIdList.length === 0) {
      fetchCallerIds();
    }
  }, [callerIdList.length, fetchCallerIds]);
  

  useEffect(() => {
    subscribe(
      "VOICE_CALLER_ID_VERIFICATION_CALLBACK_EVENT",
      (data) => {
        console.log("VOICE_CALLER_ID_VERIFICATION_CALLBACK_EVENT >> DATA-RECEIVED >>>>>", data);

        if (data && verificationCode) {
          try {
            let response = typeof data === "string" ? JSON.parse(data) : data;

            if (response?.data && typeof response?.data === "string") {
              try {
                response = JSON.parse(response?.data);
                console.log("VOICE_CALLER_ID_VERIFICATION_CALLBACK_EVENT >> PARSED >>>>>", response);

                setIsCallerIdPopupVisible(false);
                setShowVerificationCode(false);

                if (response?.status === "success") {
                  HNotification.notify({
                    type: "success",
                    message: "Success",
                    description: "Caller ID verification completed successfully! Please select the caller ID from the list.",
                  });
                  setCallerId(`${response?.callerId}`);
                  setOutgoingCallerIdSid(response?.outgoingCallerIdSid);
                } else {
                  HNotification.notify({
                    type: "error",
                    message: "Error",
                    description: "Verification failed. Check your phone number and try again.",
                  });
                }

                form.resetFields();
              } catch (e) {
                console.warn("Failed to parse nested response data:", e);
                HNotification.notify({
                  type: "error",
                  message: "Error",
                  description: "Failed to process verification response.",
                });
              }
            }
          } catch (e) {
            console.warn("Failed to parse response:", e);
            HNotification.notify({
              type: "error",
              message: "Error",
              description: "Failed to parse verification response.",
            });
          }
        }
      },
      false,
      true
    );

    return () => {
      unSubscribe("VOICE_CALLER_ID_VERIFICATION_CALLBACK_EVENT", "callerIdSettings");
    };
  }, [appProperties?.currentUser?.osyncUserId, verificationCode, subscribe, unSubscribe]);

  const callerIdOptions = [
    { label: "None", value: "none" },
    ...callerIdList?.map((callerId) => ({
      label: callerId?.callerId,
      value: callerId?.callerId + "::" + callerId?.outgoingCallerIdSid,
    })),
  ];

  const handleGenerateVerificationCode = async () => {
    const payload = {
      callerId: form.getFieldValue("callerId"),
    };
    const response = await verifyCallerId(payload);
    if (response) {
      const verificationCode = response?.callerIdVerificationCode;
      if (response?.status === "success") {
        setShowVerificationCode(true);
        setVerificationCode(verificationCode);
      } else {
        const errorMessage = response?.errorMessage;
        if (errorMessage) {
          HNotification.notify({
            type: "error",
            message: "Error",
            description: errorMessage,
          });
        } else {
          HNotification.notify({
            type: "error",
            message: "Error",
            description: "Failed to generate verification code. Please try again.",
          });
        }
      }
    }
  };

  const handleCallerIdChange = (value) => {
    if (value === "none") {
      setCallerId("none");
      setOutgoingCallerIdSid("");
      return;
    }
    const splittedValue = value?.split("::");
    const callerId = splittedValue?.[0];
    setCallerId(callerId);
    const outgoingCallerIdSid = splittedValue?.[1];
    setOutgoingCallerIdSid(outgoingCallerIdSid);
  };

  function ShowVerificationCode({ code }) {
    return (
      <div>
        <div className="pb-3">You will receive a call from Twilio to verify your phone number. Please enter the verification code.</div>
        <div className="d-flex justify-content-center align-items-center hs-h-55-px hs-border-light-violet-2 hs-border-0-25-rem hs-color-violet hs-fw-bolder hs-fs-20">
          {code}
        </div>
        <div className="pt-3">After Entering the code, Your Caller ID will be verified and activated.</div>
      </div>
    );
  }

  return (
    <>
      <div className={`d-flex flex-column w-100 ${!userSpecific ? "gap-4" : ""}`}>
        <div>
          <HSelect
          label="Caller ID"
            placeholder="Choose a Caller ID"
            options={callerIdOptions}
            customProps={{
              value: callerId ? callerId : defaultValue,
              onChange: handleCallerIdChange,
              className: userSpecific ? "border-0 outline-none w-100" : "",
            }}
            listEndComponent={showFooterContentForSelect("Add new Caller ID", () => setIsCallerIdPopupVisible(true))}
            optionListMaxHeight={200}
            notFoundContent="No caller ID found"
          />
          {userSpecific ? null : (
            <div className="hs-color-grey hs-fs-10-px pt-1">
              Caller ID is supported in all regions where{" "}
              <a href={TWILIO_CALLER_ID_HELP_URL} target="_blank" rel="noopener noreferrer">
                Twilio
              </a>{" "}
              offers verified caller ID services.
            </div>
          )}
        </div>
        
      </div>

      <Modal
        className="callerId-verification-modal"
        title={!showVerificationCode ? "Create New Caller ID" : "Verification Required"}
        open={isCallerIdPopupVisible}
        closable={showVerificationCode ? false : true}
        maskClosable={false}
        onCancel={() => {
          setIsCallerIdPopupVisible(false);
          setCallerId(null);
        }}
        footer={
          !showVerificationCode ? (
            <div className="w-100 pt-2">
              <HButton
                buttonClassName="w-100"
                prefixIcon={<div className="actionIconsSprite plusIconWhite" />}
                onClick={handleGenerateVerificationCode}
              >
                Generate Verification Code
              </HButton>
            </div>
          ) : null
        }
      >
        {showVerificationCode && <ShowVerificationCode code={verificationCode} />}
        {!showVerificationCode && (
          <div>
            <div>
              <Form form={form}>
                <Input
                  formItemLabel="Phone Number"
                  formItemName="callerId"
                  placeholder="Enter phone number"
                  required={true}
                  errorMessage="Please provide a phone number."
                  autoFocus
                  size="s"
                />
                <div className="hs-color-grey hs-fs-10-px pt-2">E.164 Format: +1234567890</div>
              </Form>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CallerIdSettings;
